import React, { useState } from 'react';
import { Box, SelectV1, ButtonV2 } from '@withjoy/joykit';
import { Add } from '@withjoy/joykit/icons';
import { MultiSelectComponent, SurveyComponent } from '../Survey.types';

interface MultiSelectRendererProps {
  component: SurveyComponent;
  value: string | string[];
  onChange: (value: string | string[]) => void;
}

export const MultiSelectRenderer: React.FC<MultiSelectRendererProps> = ({ component, value, onChange }) => {
  const multiSelectComponent = component as MultiSelectComponent;

  const getAvailableOptions = (dropdownValue: string) => {
    if (multiSelectComponent.filterConfig?.type === 'hideOtherForEmpty') {
      if (dropdownValue === '__empty__') {
        return multiSelectComponent.options.filter(option => option.value !== '');
      }
    }
    return multiSelectComponent.options;
  };

  const [dropdowns, setDropdowns] = useState<string[]>(() => {
    if (Array.isArray(value) && value.length > 0) {
      return value;
    }
    return ['__empty__'];
  });

  const handleDropdownChange = (index: number, selectedValue: string) => {
    const newDropdowns = [...dropdowns];
    newDropdowns[index] = selectedValue;
    setDropdowns(newDropdowns);

    // Get valid values (filter out empty placeholders)
    const validValues = newDropdowns.filter(val => val && val !== '__empty__');
    onChange(validValues);
  };

  const handleAddDropdown = () => {
    setDropdowns([...dropdowns, '__empty__']);
  };

  return (
    <Box>
      {/* Render existing dropdowns */}
      {dropdowns.map((dropdownValue, index) => {
        const availableOptions = getAvailableOptions(dropdownValue);

        return (
          <Box
            key={index}
            marginBottom={2}
          >
            <SelectV1
              {...multiSelectComponent}
              options={availableOptions}
              value={availableOptions.find(option => option.value === dropdownValue) || null}
              onChange={selectedOption => handleDropdownChange(index, selectedOption?.value || '')}
            />
          </Box>
        );
      })}

      {/* Add Another button */}
      <ButtonV2
        marginTop={5}
        onClick={handleAddDropdown}
        intent="neutral"
        shape="rounded"
        variant="solid"
        startIcon={<Add />}
        width="100%"
        disabled={dropdowns.some(d => !d || d === '__empty__')}
      >
        {multiSelectComponent.addButtonLabel}
      </ButtonV2>
    </Box>
  );
};
